import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    Image,
    Svg,
    Circle,
    pdf,
    Polygon,
    Path,
    Link
} from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import FooterColorLine from "Components/FooterColorLine";
import FooterColorLineData from "Services/data/footerColorLineData";
import PDFIcom from "Assets/Images/pdfImg.png";
import Compressor from "compressorjs";
import RadarChart from "Components/RadarChart";
import parse, { domToReact } from 'html-react-parser';

// const compressImage = (imageUrl) => {
//     return new Promise((resolve, reject) => {
//         fetch(imageUrl)
//             .then((response) => response.blob())
//             .then((blob) => {
//                 const img = new window.Image();
//                 const objectURL = URL.createObjectURL(blob);
//                 img.src = objectURL;

//                 img.onload = () => {
//                     new Compressor(blob, {
//                         quality: 0.1,
//                         maxWidth: 800,
//                         maxHeight: 800,
//                         success(result) {
//                             const compressedImageUrl = URL.createObjectURL(result);
//                             resolve(compressedImageUrl);
//                         },
//                         error(err) {
//                             reject(err);
//                         },
//                     });
//                 };

//                 img.onerror = (err) => reject(err);
//             })
//             .catch((err) => reject(err));
//     });
// };

const compressImage = (imageUrl) => {
    console.log(imageUrl, "image");

    return new Promise((resolve) => {
        fetch(imageUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`);
                }
                return response.blob();
            })
            .then((blob) => {

                if (!blob.type.startsWith("image/")) {
                    console.error("Not a valid image:", imageUrl, blob.type);
                    return resolve(null);
                }

                new Compressor(blob, {
                    quality: 0.1,
                    maxWidth: 800,
                    maxHeight: 800,
                    success(result) {
                        const compressedImageUrl = URL.createObjectURL(result);
                        resolve(compressedImageUrl);
                    },
                    error(err) {
                        console.error("Compressor error:", err, imageUrl);
                        resolve(null);
                    },
                });
            })
            .catch((err) => {
                console.error("Fetch failed:", imageUrl, err);
                resolve(null);
            });
    });
};



const parseScoreSafe = (raw) => {
    if (raw === null || raw === undefined) return 0;
    if (typeof raw === "string") {
        const cleaned = raw.replace("%", "").trim();
        const n = parseFloat(cleaned);
        return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
};

// const stripHtml = (html = "") => {
//     if (!html) return "";
//     return html.replace(/<[^>]+>/g, "").trim();
// };

const urlToBase64 = async (url) => {
    const response = await fetch(url, { mode: "cors" });
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

const prepareDataWithBase64 = async (data) => {
    const updatedLog = await Promise.all(
        (data.spare_parts_log || []).map(async (item) => {
            const updatedPhotos = await Promise.all(
                (item.photos || []).map(async (url) => {
                    try {
                        return await urlToBase64(url);
                    } catch (err) {
                        console.error("Image conversion failed:", url, err);
                        return null;
                    }
                })
            );
            return { ...item, photos: updatedPhotos.filter(Boolean) };
        })
    );

    return { ...data, spare_parts_log: updatedLog };
};

const groupBy = (array, key) =>
    array.reduce((result, item) => {
        const groupKey = item[key] || 'Unknown';
        if (!result[groupKey]) result[groupKey] = [];
        result[groupKey].push(item);
        return result;
    }, {});

// const renderRichText = (html) => {
//     if (!html) return null;

//     const parts = html
//         .replace(/<strong>/g, "<b>")
//         .replace(/<\/strong>/g, "</b>")
//         .split(/(<\/?[^>]+>)/g);

//     return parts.map((part, i) => {
//         if (part.startsWith("<b>")) return <Text key={i} style={{ fontWeight: "bold" }}>{parts[i + 1]}</Text>;
//         if (part.startsWith("<i>")) return <Text key={i} style={{ fontStyle: "italic" }}>{parts[i + 1]}</Text>;
//         if (part.startsWith("<ul>") || part.startsWith("</ul>")) return null;
//         if (part.startsWith("<li>")) return <Text key={i}>• {parts[i + 1]}</Text>;
//         if (part.startsWith("<br>")) return <Text key={i}>{"\n"}</Text>;
//         if (part.startsWith("<")) return null; // ignore other tags
//         return <Text key={i}>{part}</Text>;
//     });
// };


const decodeHtml = (html) =>
    html
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');

const renderRichText = (html) => {
    const cleanHtml = decodeHtml(html);

    const replace = (domNode) => {
        if (domNode.type === 'text') {
            return <Text>{domNode.data.split('')}</Text>;
        }

        if (domNode.type === 'tag') {
            const children = domToReact(domNode.children, { replace });

            switch (domNode.name) {
                case 'b':
                case 'strong':
                    return <Text style={{ fontWeight: 'bold' }}>{children}</Text>;
                case 'i':
                case 'em':
                    return <Text style={{ fontStyle: 'italic' }}>{children}</Text>;
                case 'u':
                    return <Text style={{ textDecoration: 'underline' }}>{children}</Text>;
                case 'br':
                    return <Text>{'\n'}</Text>;
                case 'p':
                case 'div':
                    return (
                        <View style={{ marginBottom: 8, width: '100%' }}>
                            <Text style={{ hyphens: 'none' }}>{children}</Text>
                        </View>
                    );
                case 'ul':
                case 'ol':
                    return (
                        <View style={{ marginBottom: 8, marginLeft: 12 }}>
                            {domToReact(domNode.children, { replace })}
                        </View>
                    );

                case 'li': {
                    const siblings = domNode.parent?.children?.filter((n) => n.name === 'li') || [];
                    const index = siblings.indexOf(domNode);
                    const listType =
                        domNode.parent?.name === 'ul' ||
                            domNode.attribs?.['data-list'] === 'bullet'
                            ? 'unordered'
                            : 'ordered';

                    const bullet = listType === 'ordered' ? `${index + 1}. ` : '• ';

                    return (
                        <View key={index} style={{ flexDirection: 'row', marginBottom: 4 }}>
                            <Text style={{ width: 12 }}>{bullet}</Text>
                            <Text style={{ flex: 1, hyphens: 'none' }}>{children}</Text>
                        </View>
                    );
                }

                default:
                    return <Text style={{ hyphens: 'none' }}>{children}</Text>;
            }
        }

        return null;
    };

    return parse(cleanHtml, { replace });
};

const analyzeImages = async (images) => {
    const promises = images.map(src => new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => {
            const isPortrait = img.height > img.width;
            resolve({ src, isPortrait });
        };
        img.onerror = () => resolve({ src, isPortrait: false });
        img.src = src;
    }));

    return Promise.all(promises);
};



const styles = StyleSheet.create({
    page: {
        padding: 30,
        backgroundColor: "#fff",
        fontSize: 11,
        fontFamily: "Helvetica",
    },

    // Typography
    h1: { fontSize: 20, color: "#0056b3", fontWeight: "bold" },
    h2: { fontSize: 16, color: "#0056b3", fontWeight: "bold", marginBottom: 6 },
    h3: { fontSize: 13, color: "#0056b3", fontWeight: "bold", marginBottom: 6 },
    p: { marginBottom: 8, lineHeight: 1.5, textAlign: "justify" },

    // Sections
    section: {
        padding: 10,
        paddingBottom: 20,
        backgroundColor: "#fff",
        borderRadius: 8,
        marginBottom: 12,
    },

    // Hero Card
    heroCard: {
        backgroundColor: "#0056b3",
        color: "white",
        padding: 15,
        borderRadius: 5,
        marginBottom: 10,
    },
    heroCardTitle: { fontSize: 18, fontWeight: "bold", marginBottom: 5 },
    heroCardScore: {
        fontSize: 16,
        fontWeight: "bold",
        backgroundColor: "#192a56",
        color: "white",
        padding: 8,
        textAlign: "center",
        borderRadius: 5,
        marginTop: 5,
    },

    // Grey box
    greyBox: {
        padding: 10,
        borderRadius: 5,
        marginBottom: 10,
    },

    // Table
    table: {
        display: "table",
        width: "100%",
        marginVertical: 10,
    },

    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#000",
    },

    tableHeader: {
        flexDirection: "row",
        backgroundColor: "#003366",
        color: "#fff",
    },

    tableCell: {
        borderRightWidth: 1,
        borderRightColor: "#000",
        padding: 6,            // ⬅ fixes cropped text
        fontSize: 10,
        textAlign: "center",
    },






    // Submodules grid
    submodulesGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 10,
        marginVertical: 10,
    },
    submoduleCard: {
        width: "45%",
        padding: 10,
        border: "1pt solid #ddd",
        borderRadius: 10,
        alignItems: "center",
        marginBottom: 10,
    },

    // Image grids
    imageGrid: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "flex-start",
        width: "100%",
        gap: 10,
    },
    image: {
        objectFit: "contain",
        borderRadius: 5,
        margin: 5,
        maxWidth: 500,
        maxHeight: 300,
    },

    pdfSection: {
        marginBottom: 10,
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",         // allows wrapping to next line
        justifyContent: "flex-start",
        gap: 10,
        marginTop: 6,
    },
    docItem: {
        fontSize: 12,
        marginBottom: 4,
    },
    docNote: {
        fontSize: 10,
        color: "#4a4a4a",
        marginBottom: 15,
        marginTop: 10
    },
    docContainer: {
        marginBottom: 15,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "30%",
    },
    pdfIcon: {
        width: 75,
        height: 75,
        marginBottom: 8
    },
});

const secondTableStyles = StyleSheet.create({
    table: { display: "table", width: "100%", marginVertical: 10 },
    tableRow: {
        flexDirection: "row",
        paddingTop: 8,
        paddingBottom: 8,
    },
    tableHeader: {
        flexDirection: "row",
        backgroundColor: "#003366",
        color: "#fff",
        textAlign: "center",
        padding: 5,
        paddingTop: 8,
        paddingBottom: 8,
    },
    tableCell: {
        flex: 1,
        // border: "1pt solid #000",
        padding: 5,
        textAlign: "center",
    },
});

const ReportPDF = ({ resultData, compressedImages, recommendations = [], chartImage }) => {
    const responseData = resultData;
    const sections = [
        { name: "BEARING MOUNTING" },
        { name: "BEARING DISMOUNTING" },
        { name: "LUBRICATION MANAGEMENT" },
        { name: "SHAFT AND BELT/CHAIN ALIGNMENT" },
        { name: "CONDITION MONITORING" },
        { name: "GEARBOX" },
    ];



    return (
        <Document>
            <Page size="A4" style={{ padding: 0, position: 'relative' }}>
                <Image
                    src="https://kg-preprod-bucket.s3.me-central-1.amazonaws.com/c3141f3d-537e-4371-9d2d-4b59cfc932e5---MA%20FIRST%20PAGE.jpg"
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                    }}
                />
            </Page>

            <Page size="A4" style={styles.page} wrap>
                {/* Executive Summary */}
                <View style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <View style={[styles.section, { marginBottom: 10 }]}>
                        <Text style={styles.h2}>Executive Summary</Text>
                        <Text style={[styles.p, { lineHeight: 1, marginTop: 12 }]}>
                            This report provides a comprehensive evaluation of the current
                            maintenance practices at{" "}
                            <Text style={{ fontWeight: "bold" }}>{responseData?.spare_parts_log?.[0]?.company_name}</Text>, based on the
                            assessment conducted through the KG Maintenance Assessment portal.The primary goal of this assessment is to identify potential areas for improvement in the
                            processes and tools used for maintaining bearings and associated equipment.
                        </Text>
                        <Text style={[styles.p, { lineHeight: 1 }]}>
                            Our experts have analyzed your responses to key questions, providing
                            insights into your bearing maintenance practices across four critical
                            areas. A performance score for each area has been generated to provide a
                            clear summary of your current maintenance practices.
                        </Text>
                        <View wrap={false} style={[styles.p, { lineHeight: 1 }]}>
                            <Text>• Scores between 50% and 100% highlight effective practices</Text>
                            <Text>• Scores below 50% indicate critical areas requiring attention</Text>
                        </View>
                    </View>

                    {/* Scores Table */}
                    <View style={[secondTableStyles.table, { marginTop: -6 }]}>
                        {responseData?.result?.some((data) =>
                            data?.performance_results?.some((cat) => cat.average_score != null)
                        ) && (
                                <>
                                    {/* Header */}
                                    <View style={secondTableStyles.tableHeader}>
                                        <Text style={[secondTableStyles.tableCell, { flex: 2, textAlign: "left" }]}>Modules</Text>
                                        <Text style={secondTableStyles.tableCell}>Scores</Text>
                                        <Text style={[secondTableStyles.tableCell, secondTableStyles.lastCell]}>Rating</Text>
                                    </View>

                                    {/* Rows */}
                                    {sections.map((section, secIndex) => {
                                        // Check if there is any valid score or rating for this section
                                        const hasValidData = responseData?.result?.some((data) =>
                                            data?.performance_results?.some((cat) =>
                                                cat.category_name === section.name && (cat.average_score != null || cat.average_score !== undefined)
                                            )
                                        );

                                        if (!hasValidData) return null; // Don't render the row if there's no valid score or rating

                                        const filteredSections = sections.filter((s) =>
                                            responseData?.result?.some((data) =>
                                                data?.performance_results?.some((cat) => cat.category_name === s.name)
                                            )
                                        );

                                        const filteredIndex = filteredSections.findIndex(
                                            (s) => s.name === section.name
                                        );


                                        return (
                                            <View key={secIndex} style={[
                                                secondTableStyles.tableRow,
                                                (filteredIndex + 1) % 2 === 0
                                                    ? { backgroundColor: "#d3d3d3" }
                                                    : {}
                                            ]}>
                                                <Text style={[secondTableStyles.tableCell, { flex: 2, textAlign: "left" }]}>{section.name}</Text>

                                                {responseData?.result?.map((data, index) =>
                                                    data?.performance_results
                                                        ?.filter((c) => c.category_name === section.name)
                                                        .map((cat) => (
                                                            <React.Fragment key={`${cat.category_name}-${index}`}>
                                                                {/* Score */}
                                                                {cat.average_score != null ? (
                                                                    <Text style={secondTableStyles.tableCell}>
                                                                        {`${cat.average_score}%`}
                                                                    </Text>
                                                                ) : (
                                                                    <Text style={secondTableStyles.tableCell}>-</Text>
                                                                )}

                                                                {/* Rating */}
                                                                <View style={[secondTableStyles.tableCell, { alignItems: "center" }]}>
                                                                    {cat.average_score != null ? (
                                                                        cat.average_score >= 50 ? (
                                                                            <Svg height="20" width="20">
                                                                                <Polygon points="10,0 20,20 0,20" fill="green" />
                                                                            </Svg>
                                                                        ) : (
                                                                            <Svg height="20" width="20">
                                                                                <Polygon points="0,0 20,0 10,20" fill="red" />
                                                                            </Svg>
                                                                        )
                                                                    ) : (
                                                                        <Text>-</Text>
                                                                    )}
                                                                </View>
                                                            </React.Fragment>
                                                        ))
                                                )}
                                            </View>
                                        );
                                    })}
                                </>
                            )}
                    </View>




                    <View style={[styles.section, { marginBottom: 12 }]}>
                        <Text style={[styles.p, { lineHeight: 1 }]}>
                            This report includes detailed observations, customized recommendations
                            and actionable solutions to address identified gaps. By implementing these
                            recommendations, your team can improve operational efficiency, enhance
                            equipment reliability and reduce downtime.
                        </Text>
                    </View>

                    <View style={{ marginTop: "auto", paddingTop: 20 }}>
                        <Text style={[
                            styles.p,
                            {
                                lineHeight: 1,
                                fontSize: 10,
                                color: "gray"
                            }
                        ]}>
                            Disclaimer: This report contains observations, analysis, comments and
                            recommendations based on the information and sample/images provided by
                            the client. The inspection was visual unless otherwise stated. KG
                            International accepts no liability for the consequences of implementation
                            of recommendations provided. Should you have any questions regarding this
                            inspection report and recommendations, please contact us.
                        </Text>
                    </View>
                </View>

                <View style={{ margin: 40, alignItems: 'center' }} break>
                    <Text style={[styles.h2, { marginBottom: 20 }]}>Overall Performance Chart</Text>
                    {chartImage ?
                        (
                            <Image
                                src={chartImage}
                                style={{ width: '100%', height: 'auto', maxHeight: 500 }}
                            />
                        ) : (
                            <Text>No chart image available</Text>
                        )}
                </View>


                {responseData?.result?.map((resultItem, categoryIndex) => (
                    <View key={categoryIndex} break>

                        {resultItem?.performance_results?.map((category, catIndex) => (
                            <View key={catIndex} wrap>


                                <View style={styles.heroCard} wrap={false}>
                                    <Text style={styles.heroCardTitle}>
                                        {`${categoryIndex + 1}. ${category.category_name}`}
                                    </Text>
                                    <Text>This score is based on your assessment</Text>
                                    <Text style={styles.heroCardScore}>
                                        {category.average_score}%
                                    </Text>
                                </View>


                                <Text style={styles.h3}>
                                    {`${categoryIndex + 1}.1 `}Sub-Module Scores
                                </Text>

                                <View style={styles.submodulesGrid}>
                                    {category.sub_categories?.map((sub, subIndex) => {
                                        const score = parseScoreSafe(sub.average_score);
                                        const r = 35;
                                        const cx = 40;
                                        const cy = 40;
                                        const circumference = 2 * Math.PI * r;
                                        const dashLength = Math.max(1, (score / 100) * circumference);
                                        const dashString = `${dashLength},${circumference}`;

                                        const circlePath = `
                                                M ${cx} ${cy - r}
                                                A ${r} ${r} 0 1 1 ${cx - 0.01} ${cy - r}
                                                `;

                                        return (
                                            <View key={subIndex} style={{
                                                ...styles.submoduleCard,
                                                minHeight: 140,
                                            }} wrap={false}>
                                                <Text style={{ marginBottom: 20 }}>
                                                    {sub.subCategory_name}
                                                </Text>

                                                <Svg height="80" width="80" viewBox="0 0 80 80">
                                                    <Path
                                                        d={circlePath}
                                                        stroke="#d3d3d3"
                                                        strokeWidth={10}
                                                        fill="none"
                                                    />

                                                    {score > 0 && (
                                                        <Path
                                                            d={circlePath}
                                                            stroke={score > 50 ? "#02cf35" : "#ff352e"}
                                                            strokeWidth={10}
                                                            fill="none"
                                                            strokeDasharray={dashString}
                                                            strokeLinecap="round"
                                                        />
                                                    )}
                                                </Svg>

                                                <Text style={{ marginTop: 15 }}>
                                                    {Math.round(score)}% vs Peers
                                                </Text>
                                            </View>
                                        );
                                    })}
                                </View>

                            </View>
                        ))}

                        {resultItem?.combined_data?.map((combinedItem, combinedIndex) => {
                            const questionNumber = `${categoryIndex + 1}.${combinedIndex + 2}`;
                            return (
                                <View key={combinedIndex}>

                                    {/* Question */}
                                    <Text style={[styles.h2, { marginTop: 5 }]}>
                                        {`${questionNumber} ${combinedItem?.question}`}
                                    </Text>

                                    {/* 1. Answer */}
                                    <Text style={styles.h3}>
                                        {`${questionNumber}.1 `}Answer
                                    </Text>

                                    {combinedItem?.answers?.map((ans, i) => {
                                        const parts = [];

                                        if (ans.bearing_size) {
                                            parts.push(ans.bearing_size);
                                        }

                                        if (ans.subOption) {
                                            parts.push(ans.subOption?.title);
                                        }

                                        const mainAnswer = ans.options?.title || ans.text_answer || "N/A";
                                        parts.push(mainAnswer);

                                        const displayText = parts.join(" – ");

                                        return (
                                            <View key={i} style={styles.greyBox}>
                                                <Text>{ans.user_name} – {displayText}</Text>
                                            </View>
                                        );
                                    })}
                                    <Text style={styles.h3}>
                                        {`${questionNumber}.2 `}Observations
                                    </Text>

                                    <View>
                                        {(() => {
                                            const notes = combinedItem?.research?.filter(
                                                (r) => r.notes && renderRichText(r.notes) !== ""
                                            );

                                            return notes?.length > 0 ? (
                                                notes.map((research, i) => (
                                                    <View key={i} style={styles.greyBox}>
                                                        <Text style={{ lineHeight: 1.15 }}>
                                                            <Text style={{ fontWeight: "bold" }}>
                                                                {`${questionNumber}.2.${i + 1}\n`}
                                                            </Text>
                                                            <Text>{renderRichText(research.notes)}</Text>
                                                        </Text>
                                                    </View>
                                                ))
                                            ) : (
                                                <Text style={{ marginBottom: 10 }}>
                                                    No Observations available
                                                </Text>
                                            );
                                        })()}
                                    </View>


                                    <Text style={styles.h3}>
                                        {`${questionNumber}.3 `}Insights
                                    </Text>

                                    <View>
                                        {(() => {
                                            const insights = combinedItem?.research?.filter(
                                                (r) => r.insights && renderRichText(r.insights) !== ""
                                            );

                                            return insights?.length > 0 ? (
                                                insights.map((research, i) => (
                                                    <View key={i} style={styles.greyBox}>
                                                        <Text style={{ lineHeight: 1.15 }}>
                                                            <Text style={{ fontWeight: "bold" }}>
                                                                {`${questionNumber}.3.${i + 1}\n`}
                                                            </Text>
                                                            <Text>{renderRichText(research.insights)}</Text>
                                                        </Text>
                                                    </View>
                                                ))
                                            ) : (
                                                <Text style={{ marginBottom: 10 }}>
                                                    No insights available
                                                </Text>
                                            );
                                        })()}
                                    </View>

                                    <Text style={styles.h3}>
                                        {`${questionNumber}.4 `}Assessment References
                                    </Text>

                                    <View style={{ marginBottom: 10 }} wrap>
                                        {combinedItem?.research?.length > 0 ? (
                                            combinedItem.research.map((research, resIndex) =>
                                                (research.upload || []).map((uploadItem, uIndex) => {

                                                    const files = uploadItem.image_url || [];

                                                    const docFiles = files.filter(url =>
                                                        /\.(pdf|docx|xlsx)$/i.test(url)
                                                    );

                                                    const compressedImages = uploadItem.compressedImages || [];

                                                    return (
                                                        <View
                                                            key={`${resIndex}-${uIndex}`}
                                                            style={{
                                                                border: "1 solid #dcdcdc",
                                                                padding: 10,
                                                                marginBottom: 12
                                                            }}
                                                            wrap
                                                        >

                                                            <Text style={{ fontWeight: "bold", marginBottom: 5 }}>
                                                                {`${questionNumber}.4.${resIndex + 1}`}
                                                            </Text>

                                                            {/* Text */}
                                                            {uploadItem.text && (
                                                                <Text style={{ marginBottom: 8, lineHeight: 1.15 }} wrap>
                                                                    {renderRichText(uploadItem.text)}
                                                                </Text>
                                                            )}

                                                            {/* COMPRESSED IMAGES */}
                                                            {compressedImages.length > 0 && (
                                                                <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                                                                    {compressedImages.map((img, imgIdx) => (
                                                                        <Image
                                                                            key={imgIdx}
                                                                            src={img?.src || img}
                                                                            style={{
                                                                                maxWidth: 250,
                                                                                maxHeight: 180,
                                                                                objectFit: "contain",
                                                                                marginBottom: 6
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </View>
                                                            )}

                                                            {/* DOCUMENTS */}
                                                            {docFiles.length > 0 && (
                                                                <View style={{ marginTop: 6 }}>
                                                                    {docFiles.map((docUrl, docIdx) => {
                                                                        const rawName = docUrl.split("/").pop();
                                                                        const fileName = rawName.includes("---")
                                                                            ? rawName.split("---").pop()
                                                                            : rawName;

                                                                        return (
                                                                            <Text key={docIdx} style={{ fontSize: 10, marginBottom: 3 }}>
                                                                                <Link src={docUrl}>{decodeURIComponent(fileName)}</Link>
                                                                            </Text>
                                                                        );
                                                                    })}
                                                                </View>
                                                            )}

                                                        </View>
                                                    );
                                                })
                                            )
                                        ) : (
                                            <Text>No assessment references available</Text>
                                        )}
                                    </View>

                                    <Text style={styles.h3}>
                                        {`${questionNumber}.5 `}Recommendations
                                    </Text>

                                    <View style={{ marginBottom: 10 }} wrap>
                                        {combinedItem?.recommendations?.length > 0 ? (
                                            combinedItem.recommendations.map((rec, recIdx) => {

                                                const files = rec?.image_url || [];

                                                const docFiles = files.filter(url =>
                                                    /\.(pdf|docx|xlsx)$/i.test(url)
                                                );

                                                const compressedImages = rec?.compressedImages || [];

                                                return (
                                                    <View
                                                        key={recIdx}
                                                        style={{
                                                            border: "1 solid #dcdcdc",
                                                            padding: 10,
                                                            marginBottom: 12
                                                        }}
                                                        wrap
                                                    >

                                                        <Text style={{ fontWeight: "bold", marginBottom: 5 }}>
                                                            {`${questionNumber}.5.${recIdx + 1}`}
                                                        </Text>

                                                        {/* Message */}
                                                        {rec?.message && (
                                                            <Text style={{ lineHeight: 1.15, marginBottom: 8 }}>
                                                                {renderRichText(rec.message)}
                                                            </Text>
                                                        )}

                                                        {/* COMPRESSED IMAGES */}
                                                        {compressedImages.length > 0 && (
                                                            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                                                                {compressedImages.map((img, imgIdx) => (
                                                                    <Image
                                                                        key={imgIdx}
                                                                        src={img?.src || img}
                                                                        style={{
                                                                            maxWidth: 250,
                                                                            maxHeight: 180,
                                                                            objectFit: "contain",
                                                                            marginBottom: 6
                                                                        }}
                                                                    />
                                                                ))}
                                                            </View>
                                                        )}

                                                        {/* DOCUMENT LIST */}
                                                        {docFiles.length > 0 && (
                                                            <View style={{ marginTop: 6 }}>
                                                                {docFiles.map((docUrl, docIdx) => {
                                                                    const rawName = docUrl.split("/").pop();
                                                                    const fileName = rawName.includes("---")
                                                                        ? rawName.split("---").pop()
                                                                        : rawName;

                                                                    return (
                                                                        <Text key={docIdx} style={{ fontSize: 10, marginBottom: 3 }}>
                                                                            <Link src={docUrl}>{decodeURIComponent(fileName)}</Link>
                                                                        </Text>
                                                                    );
                                                                })}
                                                            </View>
                                                        )}

                                                    </View>
                                                );
                                            })
                                        ) : (
                                            <Text>No recommendations available</Text>
                                        )}
                                    </View>



                                </View>
                            )

                        })}

                    </View>
                ))}





                {/* Product Offerings Table */}
                {responseData?.spare_parts_log?.length > 0 &&
                    <View style={styles.section} break>
                        <View style={styles.heroCard} wrap={false}>
                            <Text style={styles.heroCardTitle}>KG Product Offerings</Text>
                        </View>

                        <View style={styles.table}>
                            {/* Table header (fixed) */}
                            <View style={[styles.tableRow, styles.tableHeader]} fixed>
                                <Text style={[styles.tableCell, { flex: 2, borderLeftWidth: 1 }]}>Module Name</Text>
                                <Text style={[styles.tableCell, { flex: 2 }]}>Product Name</Text>
                                <Text style={[styles.tableCell, { flex: 2 }]}>Product Image</Text>
                                <Text style={[styles.tableCell, { flex: 4 }]}>Recommendations</Text>
                                <Text style={[styles.tableCell, { flex: 1 }]}>Priority</Text>
                            </View>

                            {/* Table rows */}
                            {responseData?.spare_parts_log?.map((item, idx) => (
                                <View key={idx} style={styles.tableRow}>
                                    {/* Module Name Column */}
                                    <View
                                        style={[styles.tableCell,
                                        {
                                            flex: 2,
                                            borderTopWidth: 0,
                                            borderLeftWidth: 1,
                                            justifyContent: "flex-start",
                                            alignItems: "flex-start",
                                            textAlign: "left",
                                            width: 130,
                                        }
                                        ]}>
                                        <Text style={{ marginTop: "2px" }}>{item.module_name}</Text>
                                    </View>


                                    {/* Product Name */}
                                    <View style={[styles.tableCell, { flex: 2, minWidth: 0, flexDirection: "column" }]}>
                                        <Text style={{ textAlign: "left", fontSize: 10 }}>
                                            {item.product_name}
                                        </Text>
                                    </View>


                                    {/* Product Image */}
                                    <View style={[styles.tableCell, { flex: 2, alignItems: "center" }]}>
                                        {item.photos?.length > 0 ? (
                                            item.photos.map((img, j) => {
                                                // Check if the compressed image is available in the compressedImages array
                                                const compressedImage = compressedImages?.find(
                                                    (compressedImageUrl) => compressedImageUrl.includes(img.split("/").pop())
                                                );

                                                return (
                                                    <Image
                                                        key={j}
                                                        src={compressedImage || img}  // Use compressed image if available, otherwise use the original one
                                                        style={{ width: 80, height: 80, margin: 2 }}
                                                    />
                                                );
                                            })
                                        ) : (
                                            <Text>No Image</Text>
                                        )}
                                    </View>

                                    {/* Recommendations */}
                                    <Text style={[styles.tableCell, { flex: 4 }]}>
                                        {item.recommendations}
                                    </Text>

                                    {/* Priority */}
                                    <Text style={[styles.tableCell, { flex: 1 }]}>
                                        {item.priority}
                                    </Text>
                                </View>
                            ))}
                        </View>
                    </View>}






                <View
                    fixed
                    style={{
                        bottom: 0,
                        left: 0,
                        right: 0,
                        width: "100%",
                        position: "absolute",
                        flexDirection: "row",
                    }}
                >
                    {FooterColorLineData.map((item, index) => (
                        <View
                            key={index}
                            style={{
                                flex: 1,
                                backgroundColor: item?.color,
                                height: 16,
                            }}
                        />
                    ))}
                </View>

            </Page>

        </Document>
    );
};

export const ReportDownload = forwardRef(({ resultData, scores }, ref) => {
    const downloadFileName = resultData?.assessment_details?.reference_name;
    const [isCompressing, setIsCompressing] = useState(false);
    const radarRef = useRef();
    useImperativeHandle(ref, () => ({
        async download() {
            try {
                setIsCompressing(true);

                await new Promise((res) => setTimeout(res, 2000));

                // Step 1: Get radar chart image
                const chartImage = await radarRef.current?.getImage();
                if (!chartImage) console.error("Failed to generate chart image");

                // Step 1: Compress images from researchData (from each result item)
                // const researchImages = await Promise.all(
                //     (resultData?.result || []).map(async (resultItem) => {
                //         const researchData = await Promise.all(
                //             (resultItem.researchData || []).map(async (research) => {
                //                 const uploadImages = await Promise.all(
                //                     (research.upload || []).map(async (u) => {
                //                         const images = Array.isArray(u.image_url)
                //                             ? u.image_url.filter((url) => /\.(jpg|jpeg|png|gif)$/i.test(url))
                //                             : typeof u.image_url === "string" && /\.(jpg|jpeg|png|gif)$/i.test(u.image_url)
                //                                 ? [u.image_url]
                //                                 : [];


                //                         const compressed = images.length
                //                             ? await Promise.all(images.map(img => compressImage(img)))
                //                             : []

                //                         const analyzed = compressed.length
                //                             ? await analyzeImages(compressed)
                //                             : []

                //                         return analyzed
                //                     })
                //                 )

                //                 return {
                //                     ...research,
                //                     uploadImages,
                //                 }
                //             })
                //         )

                //         return {
                //             ...resultItem,
                //             researchData,
                //         }
                //     })
                // )


                // const recommendations = await Promise.all(
                //     (resultData?.result || []).map(async (resultItem) => {
                //         const recs = await Promise.all(
                //             (resultItem.recommendations || []).map(async (rec) => {

                //                 const images = Array.isArray(rec.image_url)
                //                     ? rec.image_url.filter((url) => /\.(jpg|jpeg|png|gif)$/i.test(url))
                //                     : [];

                //                 const compressed = images.length ? await Promise.all(images.map((img) => compressImage(img))) : [];

                //                 const analyzed = compressed.length ? await analyzeImages(compressed) : [];

                //                 return {
                //                     ...rec,
                //                     compressedImages: analyzed,
                //                 };
                //             })
                //         );
                //         return recs;
                //     })
                // );

                const processedResults = await Promise.all(
                    (resultData?.result || [])?.map(async (section) => {
                        const processedQuestions = await Promise.all(
                            (section?.combined_data || [])?.map(async (questionItem) => {
                                const recommendations = await Promise.all(
                                    (questionItem?.recommendations || [])?.map(async (rec) => {
                                        const images = Array.isArray(rec.image_url)
                                            ? rec.image_url.filter((url) => /\.(jpg|jpeg|png|gif)$/i.test(url))
                                            : [];

                                        const compressed = images.length ? await Promise.all(images.map((img) => compressImage(img))) : [];

                                        const analyzed = compressed.length ? await analyzeImages(compressed) : [];

                                        return {
                                            ...rec,
                                            compressedImages: analyzed,
                                        };
                                    })
                                )

                                const researchImages = await Promise.all(
                                    (questionItem?.research || [])?.map(async (researchItem) => {
                                        const processedUploads = await Promise.all(
                                            (researchItem?.upload || [])?.map(async (uploadItem) => {
                                                const images = Array.isArray(uploadItem.image_url)
                                                    ? uploadItem.image_url.filter(url => /\.(jpg|jpeg|png|gif)$/i.test(url))
                                                    : [];

                                                const compressed = images.length
                                                    ? await Promise.all(images.map(img => compressImage(img)))
                                                    : [];

                                                const analyzed = compressed.length
                                                    ? await analyzeImages(compressed)
                                                    : [];

                                                return {
                                                    ...uploadItem,
                                                    compressedImages: analyzed,
                                                };
                                            })
                                        )
                                        return {
                                            ...researchItem,
                                            upload: processedUploads,
                                        };
                                    })
                                )
                                return {
                                    ...questionItem,
                                    recommendations,
                                    research: researchImages,
                                };

                            })
                        )
                        return {
                            ...section,
                            combined_data: processedQuestions,
                        };
                    })
                )


                // Step 2: Compress spare_parts_log images per part
                const sparePartImages = await Promise.all(
                    resultData?.spare_parts_log?.map(async (part) => {
                        const images = part.photos?.filter((file) =>
                            /\.(jpg|jpeg|png|gif)$/i.test(file)
                        );
                        const compressed = images
                            ? await Promise.all(images.map((img) => compressImage(img)))
                            : [];

                        const analyzed = compressed.length
                            ? await analyzeImages(compressed)
                            : [];
                        return { ...part, compressedPhotos: analyzed };
                    }) || []
                );


                // Step 3: Generate the PDF with the compressed images
                const blob = await pdf(
                    <>
                        <ReportPDF
                            resultData={{
                                ...resultData,
                                result: processedResults,
                                spare_parts_log: sparePartImages,
                            }}
                            chartImage={chartImage}
                        />
                    </>
                ).toBlob();

                saveAs(blob, downloadFileName);
                return blob
            } catch (error) {
                console.error("Error during download:", error);
                return null
            } finally {
                setIsCompressing(false);
            }
        },
    }));

    return (
        <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: -1,
            visibility: "hidden",
            width: "600px",
            height: "600px",
            pointerEvents: "none",
        }} >
            <RadarChart ref={radarRef} scores={scores} />
        </div>
    );
});

